import { useMatches } from 'react-router-dom';

interface RouteUiHandle {
  hideTabBar?: boolean;
}

/**
 * Whether the floating tab bar should be hidden for the current route.
 *
 * It is hidden on:
 *  - routes that explicitly opt out via `handle: { hideTabBar: true }`
 *    (full-screen forms, viewers, chat composers), and
 *  - detail / drill-down routes — any route whose matched leaf carries a URL
 *    parameter, e.g. `facilities/:facilityId` or `classifieds/:id`. These are
 *    reached by tapping into an item, carry a back button, and often pin a
 *    bottom CTA that the tab bar would otherwise sit beneath and clutter.
 *
 * Top-level tab pages (Home, Facilities, Classifieds, …) take no params, so
 * they keep the bar. New `:id` detail routes inherit this behaviour for free.
 */
export function useTabBarHidden(): boolean {
  const matches = useMatches();

  const optedOut = matches.some((match) => {
    const handle = match.handle as RouteUiHandle | undefined;
    return handle?.hideTabBar === true;
  });
  if (optedOut) return true;

  const leaf = matches[matches.length - 1];
  return leaf ? Object.keys(leaf.params).length > 0 : false;
}
